import api from './api';

export const subscriptionService = {
  // Get all subscriptions with pagination and filters
  async getSubscriptions(params = {}) {
    try {
      const queryParams = new URLSearchParams();
      
      if (params.page) queryParams.append('page', params.page);
      if (params.limit) queryParams.append('limit', params.limit);
      if (params.search) queryParams.append('search', params.search);
      if (params.status && params.status !== 'all') queryParams.append('status', params.status);
      if (params.planId) queryParams.append('planId', params.planId);
      
      const response = await api.get(`/subscriptions/users?${queryParams.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
      throw error;
    }
  },

  // Assign plan to user
  async assignPlan(assignData) {
    try {
      const response = await api.post('/subscriptions/assign', assignData);
      return response.data;
    } catch (error) {
      console.error('Error assigning plan:', error);
      throw error;
    }
  },

  // Get specific user's subscription
  async getUserSubscription(userId) {
    try {
      const response = await api.get(`/subscriptions/user/${userId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching user subscription:', error);
      throw error;
    }
  },

  // Get current user's subscription (for user dashboard)
  async getMySubscription() {
    try {
      const response = await api.get('/subscriptions/my-subscription');
      return response.data;
    } catch (error) {
      console.error('Error fetching my subscription:', error);
      throw error;
    }
  },

  // Cancel subscription
  async cancelSubscription(userId, reason = '') {
    try {
      const response = await api.put(`/subscriptions/cancel/${userId}`, { reason });
      return response.data;
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      throw error;
    }
  },

  // Extend subscription
  async extendSubscription(userId, extensionData) {
    try {
      const response = await api.put(`/subscriptions/extend/${userId}`, extensionData);
      return response.data;
    } catch (error) {
      console.error('Error extending subscription:', error);
      throw error;
    }
  },

  // Get subscription analytics
  async getAnalytics() {
    try {
      const response = await api.get('/subscriptions/analytics');
      return response.data;
    } catch (error) {
      console.error('Error fetching subscription analytics:', error);
      throw error;
    }
  },

  // Bulk update subscriptions
  async bulkUpdate(bulkData) {
    try {
      const response = await api.put('/subscriptions/bulk-update', bulkData);
      return response.data;
    } catch (error) {
      console.error('Error performing bulk update:', error);
      throw error;
    }
  },

  // Get subscription history for a user
  async getSubscriptionHistory(userId) {
    try {
      const response = await api.get(`/subscriptions/history/${userId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching subscription history:', error);
      throw error;
    }
  },

  // Reactivate expired subscription
  async reactivateSubscription(userId, reactivationData) {
    try {
      const response = await api.put(`/subscriptions/reactivate/${userId}`, reactivationData);
      return response.data;
    } catch (error) {
      console.error('Error reactivating subscription:', error);
      throw error;
    }
  },

  // Get expiring subscriptions (admin dashboard)
  async getExpiringSubscriptions(days = 7) {
    try {
      const response = await api.get(`/subscriptions/expiring?days=${days}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching expiring subscriptions:', error);
      throw error;
    }
  },

  // Get subscription revenue analytics
  async getRevenueAnalytics(period = 'monthly') {
    try {
      const response = await api.get(`/subscriptions/revenue?period=${period}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching revenue analytics:', error);
      throw error;
    }
  },

  // Export subscription data
  async exportSubscriptions(format = 'csv', filters = {}) {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('format', format);
      
      Object.keys(filters).forEach(key => {
        if (filters[key]) queryParams.append(key, filters[key]);
      });
      
      const response = await api.get(`/subscriptions/export?${queryParams.toString()}`, {
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { 
        type: format === 'csv' ? 'text/csv' : 'application/json' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `subscriptions.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      return { success: true, message: 'Export completed' };
    } catch (error) {
      console.error('Error exporting subscriptions:', error);
      throw error;
    }
  },

  // Send subscription notification
  async sendNotification(userIds, notificationData) {
    try {
      const response = await api.post('/subscriptions/notifications', {
        userIds,
        ...notificationData
      });
      return response.data;
    } catch (error) {
      console.error('Error sending notification:', error);
      throw error;
    }
  },

  // Get subscription statistics for dashboard
  async getSubscriptionStats() {
    try {
      const response = await api.get('/subscriptions/stats');
      return response.data;
    } catch (error) {
      console.error('Error fetching subscription stats:', error);
      throw error;
    }
  },

  // Validate subscription status
  async validateSubscription(userId) {
    try {
      const response = await api.get(`/subscriptions/validate/${userId}`);
      return response.data;
    } catch (error) {
      console.error('Error validating subscription:', error);
      throw error;
    }
  },

  // Update subscription preferences
  async updateSubscriptionPreferences(userId, preferences) {
    try {
      const response = await api.put(`/subscriptions/preferences/${userId}`, preferences);
      return response.data;
    } catch (error) {
      console.error('Error updating subscription preferences:', error);
      throw error;
    }
  }
};
